import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';

import { BlurFade } from '@/components/Common/BlurFade';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import type { HeySynclyChat, HeySynclyChatMode } from '@/entities/hey-syncly';
import { cn } from '@/lib/cn';
import { Button } from '@/shared/ui';
import useUserStore from '@/stores/UserStore';
import color from '@/styles/colors/color';

import { useHeySynclyPageStore } from '../../../model/hey-syncly-page-store';
import ChatInput from './chat-input';

const ChatInputContainer = (props: {
  chat: HeySynclyChat | null;
  chatMode: HeySynclyChatMode;
  onChatModeChange: (nextMode: HeySynclyChatMode) => void;
  onScrollToBottom: () => void;
  showScrollToBottomButton: boolean;
}) => {
  const {
    chat,
    chatMode,
    onChatModeChange,
    onScrollToBottom,
    showScrollToBottomButton,
  } = props;
  const currentChatAlias = useHeySynclyPageStore(
    (state) => state.currentChatAlias,
  );
  const hasActiveChat = currentChatAlias !== null;
  return (
    <div
      className={cn(
        'flex w-full max-w-219 flex-col items-center justify-center gap-2.5 transition-[top,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] px-6',
        hasActiveChat
          ? 'absolute top-[calc(100%)] left-1/2 -translate-x-1/2 -translate-y-full bg-white pb-5'
          : 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
      )}
    >
      <div className="relative flex flex-col w-full gap-10">
        {hasActiveChat && (
          <div
            className="absolute -top-5 left-0 h-10 w-full"
            style={{
              backgroundImage:
                'linear-gradient(180deg,color(from #fff srgb r g b/0),color(from #fff srgb r g b/100) 60%)',
            }}
          />
        )}
        {hasActiveChat && showScrollToBottomButton && (
          <Button
            aria-label="Scroll to bottom"
            className="absolute -top-20 left-1/2 z-20 h-10 w-10 -translate-x-1/2 rounded-full border-border bg-background text-foreground shadow-md hover:bg-accent hover:text-accent-foreground"
            onClick={onScrollToBottom}
            size="icon"
            type="button"
            variant="outline"
          >
            <ArrowDown className="size-4" />
          </Button>
        )}
        <ToggleStage status={!hasActiveChat}>
          <WelcomeMessage />
        </ToggleStage>
        <ChatInput
          chat={chat}
          chatMode={chatMode}
          onChatModeChange={onChatModeChange}
        />
      </div>
    </div>
  );
};

export default ChatInputContainer;
const gradientStyle = `linear-gradient(90deg, ${color.foreground} 0%, ${color.foreground} 20%, ${color.brand600} 45%, ${color.brand600} 55%, ${color.foreground} 80%, ${color.foreground} 100%)`;

const WelcomeMessage = () => {
  const user = useUserStore((state) => state.user);
  return (
    <div className="flex flex-col items-center justify-center gap-3 pt-80 2xl:pt-0">
      <BlurFade
        delay={0.3}
        yOffset={10}
        className="flex flex-col gap-2 text-center"
      >
        <motion.h1
          style={{
            background: gradientStyle,
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            lineHeight: 1.6,
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            repeatDelay: 5,
            ease: 'easeOut',
          }}
          animate={{
            backgroundPosition: ['-50% 0%', '-250% 0%'],
          }}
          className="text-5xl font-medium leading-none text-foreground"
        >
          Hey, {user?.firstName}
        </motion.h1>
        <span className="text-base text-muted-foreground">
          What would you like to discover today?
        </span>
      </BlurFade>
    </div>
  );
};
